import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";
import type {
  OutgoingIntakeAnswer,
  OutgoingIntakeDuplicateMatch,
  OutgoingIntakeFile,
  OutgoingIntakeSubmission,
  WelcomeVideoTrim
} from "./types/outgoingIntake";

/**
 * Trimmed prior-submission data for the public intake, used to render the
 * cross-revision comment thread (client comments + attorney requested-change
 * notes) on each question. Only comment/note-bearing entries are returned.
 */
export type PublicIntakeRevisionEntry = {
  revision_number?: number | null;
  submitted_at?: unknown;
  reviewed_at?: unknown;
  answers?: Array<{
    question_id: string;
    repeatable_group_id?: string | null;
    repeatable_group_instance_id?: string | null;
    comment?: string | null;
  }>;
  decisions?: Array<{
    question_id: string;
    repeatable_group_id?: string | null;
    repeatable_group_instance_id?: string | null;
    status?: string | null;
    note?: string | null;
  }>;
};

/** A value the firm already has for a question, for the client to confirm. */
export type PublicIntakePrefill = {
  value: unknown;
  source?: { kind: string; label?: string | null } | null;
};

export type PublicOutgoingIntakeResponse = {
  outgoing_intake: {
    outgoing_intake_id: string;
    title: string;
    /** The title without the client's name the firm adds for itself. */
    client_title?: string | null;
    status: string;
    schema: unknown[];
    expires_at?: unknown;
    latest_submission_id?: string | null;
    progress_answers?: OutgoingIntakeAnswer[] | null;
    progress_saved_at?: unknown;
    law_firm_name?: string | null;
    client_language?: string | null;
    law_firm_logo_url?: string | null;
    enable_whitelabel_foreign_national_intake?: boolean | null;
    foreign_national_first_name?: string | null;
    foreign_national_last_name?: string | null;
    foreign_national_avatar_url?: string | null;
    attorney_name?: string | null;
    attorney_avatar_url?: string | null;
    attorney_note?: string | null;
    welcome_video_url?: string | null;
    welcome_video_trim?: WelcomeVideoTrim | null;
    enable_firm_branding?: boolean | null;
    intake_background_color?: string | null;
    /** Keyed by question id; only unanswered top-level questions. */
    prefill?: Record<string, PublicIntakePrefill> | null;
    /** "firm" when a signed-in attorney opened the client's link. */
    viewer?: "client" | "firm" | null;
  };
  latest_submission?: OutgoingIntakeSubmission;
  revision_history?: PublicIntakeRevisionEntry[];
};

export const PublicOutgoingIntakesAPI = {
  async get(token: string): Promise<PublicOutgoingIntakeResponse> {
    try {
      const response = await axiosInstance.get<PublicOutgoingIntakeResponse>(
        `/public/outgoing_intakes/${token}`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async upload(token: string, file: File): Promise<OutgoingIntakeFile> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post<{ file: OutgoingIntakeFile }>(
        `/public/outgoing_intakes/${token}/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data.file;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async submit(
    token: string,
    payload: {
      answers: OutgoingIntakeAnswer[];
      previous_submission_id?: string | null;
    }
  ): Promise<{ submission: OutgoingIntakeSubmission }> {
    try {
      const response = await axiosInstance.post<{
        submission: OutgoingIntakeSubmission;
      }>(`/public/outgoing_intakes/${token}/submit`, payload);
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  /** The intake's question text in a portal language, keyed like
   * `{node_id}.prompt`, `{node_id}.opt.{option_id}`, `title`, `note`. */
  async translation(
    token: string,
    language: string
  ): Promise<{ language: string; strings: Record<string, string> }> {
    try {
      const response = await axiosInstance.get<{
        language: string;
        strings: Record<string, string>;
      }>(
        `/public/outgoing_intakes/${token}/translation/${encodeURIComponent(
          language
        )}`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async saveProgress(
    token: string,
    payload: { answers: OutgoingIntakeAnswer[] }
  ): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.post<{ success: boolean }>(
        `/public/outgoing_intakes/${token}/progress`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  async getFile(
    token: string,
    fileId: string
  ): Promise<{ filePath: string; data: string }> {
    try {
      const response = await axiosInstance.get<{
        filePath: string;
        data: string;
      }>(`/public/outgoing_intakes/${token}/files/${fileId}/file`);
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  async getFileStatus(
    token: string,
    fileId: string
  ): Promise<OutgoingIntakeFile> {
    try {
      const response = await axiosInstance.get<{ file: OutgoingIntakeFile }>(
        `/public/outgoing_intakes/${token}/files/${fileId}/status`
      );
      return response.data.file;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  async getFilePreview(token: string, fileId: string): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `/public/outgoing_intakes/${token}/files/${fileId}/preview`,
        {
          responseType: "blob"
        }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  async getDuplicateFilePreview(
    token: string,
    fileId: string,
    match: OutgoingIntakeDuplicateMatch
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `/public/outgoing_intakes/${token}/files/${fileId}/duplicate-preview`,
        {
          params: {
            source: match.source,
            duplicate_id: match.id
          },
          responseType: "blob"
        }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  /**
   * Ask for the latest link by email. It goes to the address the firm has on
   * file, never one typed here, so knowing an intake id can't redirect it.
   */
  async resendLink(outgoingIntakeId: string): Promise<void> {
    try {
      await axiosInstance.post(
        `/public/outgoing_intakes/${outgoingIntakeId}/resend_link`,
        {}
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  async deleteFile(token: string, fileId: string): Promise<void> {
    try {
      await axiosInstance.delete(
        `/public/outgoing_intakes/${token}/files/${fileId}`
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
